import { t } from "../../i18n";
import { CheckCircle2, LoaderCircle, Terminal, XCircle } from "lucide-react";
import { useMemo, useState } from "react";
import { CopyButton } from "../CopyButton";
import { asRecord, stringValue } from "./ToolResultView.utils";

type ShellResultProps = {
  argumentsValue: unknown;
  result: unknown;
  error?: string;
  status?: string;
};

function shellText(value: unknown) {
  return typeof value === "string" ? value : "";
}

export function ShellResult({
  argumentsValue,
  result,
  error,
  status,
}: ShellResultProps) {
  const input = asRecord(argumentsValue) ?? {};
  const output = asRecord(result) ?? {};
  const command = stringValue(input.command);
  const explanation = stringValue(input.explanation);
  const cwd = stringValue(input.cwd);
  const stdout = shellText(output.stdout);
  const stderr = shellText(output.stderr);
  const code = typeof output.code === "number" ? output.code : null;
  const truncated = output.truncated === true;
  const waiting =
    status === "awaiting-approval" || status === "awaiting-question";
  const running =
    !error &&
    !waiting &&
    (status === "running" || (code === null && !stdout && !stderr));
  const failed =
    Boolean(error) ||
    (!running && !waiting && (code !== null ? code !== 0 : Boolean(stderr)));
  const [stream, setStream] = useState<"stdout" | "stderr">(
    stderr && !stdout ? "stderr" : "stdout",
  );
  const visibleOutput = stream === "stderr" ? stderr : stdout;
  const combinedOutput = useMemo(
    () => [stdout, stderr].filter(Boolean).join("\n"),
    [stderr, stdout],
  );

  return (
    <div
      className={`overflow-hidden rounded-2xl border ${
        failed
          ? "border-red-200 bg-red-50/60 dark:border-red-900/60 dark:bg-red-950/20"
          : "border-stone-200 bg-stone-950/[0.03] dark:border-white/10 dark:bg-white/[0.03]"
      }`}
    >
      <div className="border-b border-stone-200/80 p-3 dark:border-white/10">
        <div className="flex items-start gap-2">
          <div className="grid size-8 flex-none place-items-center rounded-lg bg-stone-900 text-stone-100 dark:bg-white/10 dark:text-stone-200">
            {running ? (
              <LoaderCircle size={15} className="animate-spin" />
            ) : failed ? (
              <XCircle size={15} />
            ) : (
              <Terminal size={15} />
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2 text-sm font-semibold text-stone-800 dark:text-stone-100">
              <span>
                {waiting
                  ? t("Approval needed")
                  : running
                    ? t("Running command")
                    : failed
                      ? t("Command failed")
                      : t("Command completed")}
              </span>
              {!running && !failed && (
                <CheckCircle2 size={14} className="text-emerald-600" />
              )}
            </div>
            {explanation && (
              <p className="mt-1 text-xs leading-5 text-stone-500 dark:text-stone-400">
                {explanation}
              </p>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <CopyButton value={command} label={t("Copy command")} />
          </div>
        </div>
        <div className="mt-3 overflow-hidden rounded-xl bg-[#101312] text-stone-200 shadow-inner">
          <div className="flex items-center gap-2 border-b border-white/10 px-3 py-2 text-[11px] text-stone-400">
            <span className="text-emerald-400">$</span>
            <code className="min-w-0 flex-1 overflow-x-auto font-mono whitespace-pre">
              {command || "shell command"}
            </code>
          </div>
          {cwd && (
            <div className="border-b border-white/10 px-3 py-1.5 font-mono text-[10px] text-stone-500">
              {cwd}
            </div>
          )}
        </div>
      </div>

      <div className="p-3">
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-1 rounded-lg bg-stone-100 p-1 text-[11px] dark:bg-white/5">
            <button
              type="button"
              className={`rounded-md px-2 py-1 font-semibold ${stream === "stdout" ? "bg-white text-stone-800 shadow-sm dark:bg-white/10 dark:text-stone-100" : "text-stone-500"}`}
              onClick={() => setStream("stdout")}
              disabled={!stdout}
            >
              {t("Output")}
            </button>
            <button
              type="button"
              className={`rounded-md px-2 py-1 font-semibold ${stream === "stderr" ? "bg-white text-red-700 shadow-sm dark:bg-white/10 dark:text-red-300" : "text-stone-500"}`}
              onClick={() => setStream("stderr")}
              disabled={!stderr}
            >
              {t("Errors")}
            </button>
          </div>
          <CopyButton value={combinedOutput} label={t("Copy output")} />
        </div>
        {error && (
          <div className="mb-2 rounded-xl border border-red-300/60 bg-red-950/30 p-3 text-xs leading-5 whitespace-pre-wrap text-red-200">
            {error}
          </div>
        )}
        {visibleOutput ? (
          <pre
            className={`max-h-96 overflow-auto rounded-xl bg-[#101312] p-3 font-mono text-[11px] leading-5 whitespace-pre-wrap ${stream === "stderr" ? "text-red-300" : "text-stone-200"}`}
          >
            {visibleOutput}
          </pre>
        ) : (
          <div className="rounded-xl border border-dashed border-stone-200 px-3 py-5 text-center text-xs text-stone-500 dark:border-white/10 dark:text-stone-400">
            {waiting
              ? t("Waiting for approval…")
              : running
                ? t("Waiting for command output…")
                : t("Command completed with no output")}
          </div>
        )}
        {truncated && (
          <p className="mt-2 text-[11px] text-amber-700 dark:text-amber-300">
            {t(
              "Output was truncated. Copy output contains the visible portion.",
            )}
          </p>
        )}
        {!running && code !== null && (
          <div
            className={`mt-3 flex items-center justify-between text-xs font-semibold ${failed ? "text-red-700 dark:text-red-300" : "text-emerald-700 dark:text-emerald-300"}`}
          >
            <span>{failed ? t("Command failed") : t("Command succeeded")}</span>
            <code className="font-mono">
              {t("Exit code {{code}}", { code })}
            </code>
          </div>
        )}
      </div>
    </div>
  );
}
